import {ChangeDetectionStrategy, Component, inject, signal, computed} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {ChallengeAlert} from 'src/app/core/model/project';
import {AddAlertAction, UpdateAlertAction} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {selectChallengeBuilderProject} from 'src/app/core/store/challenge-builder/challenge-builder.selector';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphTimeSelectorComponent} from 'src/app/shared/design/oph-time-selector/oph-time-selector.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {CbDialogFooterComponent, CbDialogHeaderComponent} from '../cb-dialog-parts.component';

interface DialogData {
  alert?: ChallengeAlert;
}

interface DayOption {
  label: string;
  value: number;
}

@Component({
  selector: 'cb-alert-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    CbDialogHeaderComponent,
    CbDialogFooterComponent,
    OphTimeSelectorComponent,
    OphXButtonComponent,
  ],
  templateUrl: './cb-alert-dialog.component.html',
  styleUrl: './cb-alert-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbAlertDialogComponent {
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private timezoneService = inject(TimezoneService);
  readonly dialogRef = inject(MatDialogRef<CbAlertDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA, {optional: true});

  project = this.store$.selectSignal(selectChallengeBuilderProject);

  repeatDays = signal<number[]>(this.data?.alert?.days || []);
  alertStart = signal<string>(this.data?.alert?.time || this.getDefaultTime());
  alertText = signal<string>(this.data?.alert?.message || '');
  loading = signal(false);
  showTimeSelector = signal(false);

  startTimeForm = new FormGroup({
    hours: new FormControl<string>('12'),
    minutes: new FormControl<string>('00'),
    ampm: new FormControl<string>('AM'),
  });

  readonly maxMessageLength = 140;

  readonly dayOptions: DayOption[] = [
    {label: 'M', value: 0},
    {label: 'T', value: 1},
    {label: 'W', value: 2},
    {label: 'T', value: 3},
    {label: 'F', value: 4},
    {label: 'S', value: 5},
    {label: 'S', value: 6},
  ];

  isEditMode = computed(() => !!this.data?.alert?._id);
  titleVariable = computed(() => (this.isEditMode() ? 'Edit a' : 'New'));
  messageLength = computed(() => this.alertText().length);
  formattedTime = computed(() => {
    const timeValue = this.alertStart();
    if (!timeValue) return '';
    return moment(timeValue).tz(this.timezoneService.getTimeZone()).format('h:mm A');
  });

  saveDisabled = computed(() => {
    const days = this.repeatDays();
    const messageValue = this.alertText().trim();
    const timeValue = this.alertStart();
    return days.length === 0 || !messageValue || !timeValue;
  });

  private getDefaultTime(): string {
    const now = moment().tz(this.timezoneService.getTimeZone());
    now.set({hour: 8, minute: 0, second: 0, millisecond: 0});
    return this.timezoneService.convertDateToUtcString(now.toDate());
  }

  isDaySelected(day: number): boolean {
    return this.repeatDays().includes(day);
  }

  toggleDay(day: number): void {
    const current = this.repeatDays();
    if (current.includes(day)) {
      this.repeatDays.set(current.filter(d => d !== day));
    } else {
      this.repeatDays.set([...current, day].sort((a, b) => a - b));
    }
  }

  onAlertTextChange(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.alertText.set(value);
  }

  onTimeClick(): void {
    const alertStartValue = this.alertStart();
    if (alertStartValue) {
      const alertStartMoment = moment(alertStartValue).tz(this.timezoneService.getTimeZone());
      const hours = alertStartMoment.hours();
      const displayHours = hours % 12 === 0 ? 12 : hours % 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const minutes = alertStartMoment.minutes();

      this.startTimeForm.patchValue({
        hours: displayHours.toString(),
        minutes: minutes.toString().padStart(2, '0'),
        ampm,
      });
    }
    this.showTimeSelector.set(true);
  }

  closeTimeSelector(): void {
    const hours = +this.startTimeForm.value.hours;
    const adjustedHours =
      this.startTimeForm.value.ampm === 'PM' ? (hours === 12 ? 12 : hours + 12) : hours === 12 ? 0 : hours;

    const alertStart = moment()
      .tz(this.timezoneService.getTimeZone())
      .set('hours', adjustedHours)
      .set('minutes', +this.startTimeForm.value.minutes)
      .set('seconds', 0)
      .utc()
      .format();

    this.alertStart.set(alertStart);
    this.showTimeSelector.set(false);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const projectId = this.project()?._id;
    const daysValue = this.repeatDays();
    const timeValue = this.alertStart();
    const messageValue = this.alertText().trim();

    if (!projectId || daysValue.length === 0 || !timeValue || !messageValue) {
      return;
    }

    this.loading.set(true);

    const alert: ChallengeAlert = {
      days: daysValue,
      time: timeValue,
      message: messageValue,
      active: this.data?.alert?.active ?? true,
    };

    if (this.data?.alert?._id) {
      alert._id = this.data.alert._id;
      this.store$.dispatch(
        new UpdateAlertAction({
          projectId,
          alert,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    } else {
      this.store$.dispatch(
        new AddAlertAction({
          projectId,
          alert,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    }
  }

  private onSaveSuccess(): void {
    this.loading.set(false);
    this.dialogRef.close(true);
  }

  private onSaveFailure(error: Error): void {
    this.loading.set(false);
    this.messageService.add(error.message || 'Failed to add alert', 'error');
  }
}
